import { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Wand2 } from 'lucide-react';
import { generateSampleJsonString } from '@shared/utils/json-schema-sample';
import { toast } from 'sonner';

interface MCPTestMessageFieldProps {
  value: string;
  onChange: (value: string) => void;
  nodeData: any;
}

export function MCPTestMessageField({ value, onChange, nodeData }: MCPTestMessageFieldProps) {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = () => {
    setIsGenerating(true);
    try {
      const inputSchema = nodeData?.parameters?.inputSchema;
      
      if (!inputSchema) {
        toast.error('No input schema defined', {
          description: 'Please define an Input Schema first to generate test input'
        });
        return;
      }

      // Parse the schema if it's a string
      let schema = inputSchema;
      if (typeof inputSchema === 'string') {
        try {
          schema = JSON.parse(inputSchema);
        } catch (e) {
          toast.error('Invalid input schema', {
            description: 'The input schema is not valid JSON'
          });
          return;
        }
      }

      const sampleJson = generateSampleJsonString(schema);
      onChange(sampleJson);
      toast.success('Test input generated', {
        description: 'Sample JSON created from your input schema'
      });
    } catch (error) {
      // console.error('Error generating test input:', error);
      toast.error('Failed to generate test input', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Test Input</label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleGenerate}
          disabled={isGenerating}
          className="h-7 text-xs"
        >
          <Wand2 className="h-3 w-3 mr-1" />
          Generate from Schema
        </Button>
      </div>
      <Textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter test JSON matching your tool's input schema"
        className="font-mono text-xs min-h-[120px]"
      />
      <p className="text-xs text-muted-foreground">
        Test input data to use when testing this workflow in the editor (not used for actual MCP calls)
      </p>
    </div>
  );
}
